import type {
  JsonData,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
  OdinDataSender,
  OdinKey,
  TwinColumnSelectRef,
} from '@myosh/odin-components';
import { useEffect, useRef, useState } from 'react';
import type { Control } from 'react-hook-form';
import type { LookupType } from '../../../@types/forms';
import type { FieldOptionsApiProps, LookupTypeFieldApiProps } from '../../../@types/options';
import useLookupTypeField from '../../../hooks/use-lookup-type-field';
import { useLazyFieldOptionsQuery } from '../../../redux/services/field';
import { isWebView } from '../../../redux/util/webview-base-query';
import { forceAssert } from '../../../utils/type-guards';
import { filterByParentFieldOptionId, parseDependsOnValue } from '../../../utils/webview-field-utils';
import useFetchPersonFieldOptions from '../person/use-fetch-person-field-options';

/**
 * A hook that handles all aspects for fetching the twin columns fields data.
 *
 * @param textFieldKey the fields 'text' key propery (i.e. 'text')
 * @param id the 'id' of the field in the form
 * @param lookupType the defined lookup type, currently only `WITH_FIELD_KEY` and `WITH_HIERARCHY_FIELD` are supported
 * @param lookupKeyId the 'id' of the form field from which the value should be read.
 * @param lookupHierarchyId the 'id' of the hierarchy from which the value should be read.
 *
 */
const useSupportTwinColumnSelectFunctionalities = (
  textFieldKey: string,
  id?: OdinKey,
  control?: Control,
  lookupType?: LookupType,
  lookupKeyId?: number,
  lookupHierarchyId?: number,
  formId?: number
) => {
  const [options, setOptions] = useState<OdinDataRetrievalOptions>();
  const subscriberRef = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const twinColumnRef = useRef<TwinColumnSelectRef>(null);
  const dataRequestedRef = useRef<boolean | undefined>(undefined);

  const [hierarchyFieldId, setHierarchyFieldId] = useState<number>();
  const [keyValues, setKeyValues] = useState<string>();

  const fetchPersonFieldOptions = useFetchPersonFieldOptions(textFieldKey);
  const [getFieldOptions] = useLazyFieldOptionsQuery();

  const {
    shouldUseLookup,
    hierarchyFieldId: resolvedHierarchyFieldId,
    keyValue: resolvedKeyValue,
  } = useLookupTypeField(control, lookupType, lookupKeyId, lookupHierarchyId);

  useEffect(() => {
    if (shouldUseLookup && hierarchyFieldId !== resolvedHierarchyFieldId) {
      twinColumnRef.current?.clearOptionValues();
      twinColumnRef.current?.clearCache();
      setHierarchyFieldId(resolvedHierarchyFieldId);
    }
  }, [shouldUseLookup, resolvedHierarchyFieldId, hierarchyFieldId]);

  useEffect(() => {
    if (shouldUseLookup && keyValues !== resolvedKeyValue) {
      twinColumnRef.current?.clearOptionValues();
      twinColumnRef.current?.clearCache();
      setKeyValues(resolvedKeyValue);
    }
  }, [shouldUseLookup, resolvedKeyValue]);

  useEffect(() => {
    if (id && options && options.fieldSubType === 'TWINCOLUMNSELECTPERSONFIELD') {
      if (shouldUseLookup) {
        if (hierarchyFieldId || keyValues) {
          fetchAndSetPersonFieldData(id, { ...options, hierarchyFieldId, keyValue: keyValues });
        } else {
          subscriberRef.current?.sendData();
          dataRequestedRef.current = false;
        }
      } else {
        fetchAndSetPersonFieldData(id, options);
      }
    }
  }, [id, options, shouldUseLookup, keyValues, resolvedHierarchyFieldId]);

  useEffect(() => {
    if (options?.fieldId && options.fieldSubType === 'TWINCOLUMNSELECT') {
      if (options.fieldFilters && options.fieldFilters.text) {
        options.fieldFilters['caption'] = options.fieldFilters.text;
        delete options.fieldFilters.text;
      }
      const inWebView = isWebView();
      // Parse dependsOn once — used for both the server-side API param and local WebView filtering
      const parsedDependsOn = parseDependsOnValue(
        options.fieldFilters?.parentField?.value as string | number | undefined
      );
      const _options = {
        queryId: Number(options.fieldId),
        formId: formId ?? 0,
        limit: options.getAllData || inWebView ? -1 : options.pageSize || 50,
        dependsOn: inWebView ? undefined : parsedDependsOn,
        filters: options.fieldFilters,
        next: inWebView ? undefined : (options.customProperties?.next as string),
      };

      // In WebView mode, use the parsed value for local parentFieldOptionId filtering
      const localDependsOnFilter = inWebView ? parsedDependsOn : undefined;

      if (shouldUseLookup) {
        fetchAndSetOptionsData(
          {
            ..._options,
            keyValue: keyValues,
            hierarchyFieldId: resolvedHierarchyFieldId,
          },
          localDependsOnFilter,
          inWebView,
          options.requestId
        );
      } else {
        fetchAndSetOptionsData(_options, localDependsOnFilter, inWebView, options.requestId);
      }
    }
  }, [options, shouldUseLookup, resolvedHierarchyFieldId, keyValues, formId]);

  const fetchAndSetPersonFieldData = (id: OdinKey, options: OdinDataRetrievalOptions & LookupTypeFieldApiProps) => {
    if (options.fieldSubType === 'TWINCOLUMNSELECTPERSONFIELD') {
      fetchPersonFieldOptions(id, options)
        .then((data) => {
          if (data) {
            subscriberRef.current?.sendData({ data: forceAssert<JsonData>(data), requestId: options.requestId });
          } else {
            subscriberRef.current?.sendData();
          }
        })
        .catch(() => subscriberRef.current?.sendData())
        .finally(() => (dataRequestedRef.current = false));
    }
  };

  const fetchAndSetOptionsData = (
    fieldOptions: FieldOptionsApiProps,
    localDependsOnFilter: number | undefined,
    inWebView: boolean,
    requestId?: string
  ) => {
    if (
      options?.fieldSubType === 'TWINCOLUMNSELECT' &&
      (options.page === 1 || (!inWebView && options?.customProperties?.next) || options?.getAllData)
    ) {
      getFieldOptions(fieldOptions, true)
        .unwrap()
        .then((result) => {
          let data = result.items ?? [];
          if (localDependsOnFilter !== undefined) {
            data = filterByParentFieldOptionId(data, localDependsOnFilter);
          }
          const formattedData = data.map((item) => {
            return {
              value: item.id,
              text: item.caption,
            };
          });
          subscriberRef.current?.sendData({
            data: forceAssert<JsonData>(formattedData),
            requestId,
            customProperties: { next: inWebView ? undefined : result.next },
          });
        })
        .catch(() => subscriberRef.current?.sendData());
    } else {
      subscriberRef.current?.sendData();
    }
  };

  return {
    setOptions,
    subscriberRef,
    dataRequestedRef,
    twinColumnRef,
  };
};

export default useSupportTwinColumnSelectFunctionalities;
